import { useEffect, useState, memo } from 'react';

import { AssignedWorkflowPhaseDto } from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import LearnMoreDialog from 'react-components/workflows/learn-more-dialog';
import WorkflowCompletionDialog from 'react-components/workflows/workflow-completion-dialog';
import { ProgressContainerWithSpinner } from 'react-components/workflows/workflow-dialog-shared.constants';
import AttachWorkflowToEventDialog from 'react-components/workflows/workflow-event-dialog';
import {
  deleteAssignedWorkflowScheduledEvent,
  getAssignedWorkflowPhaseById,
  selectAssignedWorkflowPhaseById,
} from 'redux/slices/workflowSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import InfoCard, { BackgroundStyles } from 'toolkit/info-card/info-card';
import { Button, TaggableChip } from '@act/storybook';

interface WorkflowCardProps {
  assignedWorkflowPhase?: AssignedWorkflowPhaseDto;
  assignedWorkflowPhaseId?: number;
  gettingStarted: boolean;
  assignedWorkflowScheduledEventId?: number;
}

const WorkflowCardComponent = ({
  assignedWorkflowPhaseId,
  gettingStarted,
  assignedWorkflowScheduledEventId,
}: WorkflowCardProps) => {
  let primaryOption: React.ReactElement;
  let secondaryOption: React.ReactElement;

  const dispatch = useAppDispatch();

  const [learnOpen, setLearnOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);

  const [cardMode, setCardMode] = useState('');

  const assignedWorkflowPhase = useAppSelector(
    selectAssignedWorkflowPhaseById(assignedWorkflowPhaseId)
  );

  useEffect(() => {
    if (!assignedWorkflowPhase && assignedWorkflowPhaseId) {
      dispatch(
        getAssignedWorkflowPhaseById({
          params: {
            assignedWorkflowPhaseId: assignedWorkflowPhaseId,
          },
        })
      );
    }
  }, [assignedWorkflowPhase]);

  useEffect(() => {
    let mode = gettingStarted ? 'start' : 'schedule';
    if (assignedWorkflowPhase?.completedAt) mode = 'completed';

    setCardMode(mode);
  }, [assignedWorkflowPhase]);

  if (!assignedWorkflowPhase) return <ProgressContainerWithSpinner />;

  function handleClose(dialog: string) {
    return () => {
      if (dialog === 'learn') setLearnOpen(false);
      if (dialog === 'schedule') setScheduleOpen(false);
      if (dialog === 'workflow') setWorkflowOpen(false);
    };
  }

  const deleteAssignedWorkflow = () => {
    dispatch(
      deleteAssignedWorkflowScheduledEvent({
        params: {
          assignedWorkflowScheduledEventId: assignedWorkflowScheduledEventId,
        },
      })
    );

    setCardMode('unscheduled');
  };

  const description =
    cardMode === 'unscheduled'
      ? 'A reminder to reschedule this task was added to your feed'
      : assignedWorkflowPhase?.workflowPhase?.description;

  let backgroundStyle: BackgroundStyles = 'default';
  let chip;
  switch (cardMode) {
    case 'start':
      backgroundStyle = 'start';
      primaryOption = (
        <Button
          color="primary"
          variant="text"
          aria-label="Reschedule"
          onClick={() => deleteAssignedWorkflow()}
        >
          Remind me later
        </Button>
      );
      secondaryOption = (
        <Button
          color="primary"
          variant="text"
          aria-label="Start Workflow Phase"
          onClick={() => setWorkflowOpen(true)}
        >
          Start Now
        </Button>
      );
      break;
    case 'completed':
      backgroundStyle = 'completed';
      break;
    case 'schedule':
      primaryOption = (
        <Button
          color="primary"
          variant="text"
          aria-label="Schedule Now"
          onClick={() => setScheduleOpen(true)}
        >
          Schedule now
        </Button>
      );
      secondaryOption = (
        <Button
          color="primary"
          variant="text"
          aria-label="Open Learn more dialog"
          onClick={() => setLearnOpen(true)}
        >
          Learn more
        </Button>
      );
      if (DateTime.fromMillis(assignedWorkflowPhase.dueDate) < DateTime.now()) {
        chip = <TaggableChip isTag color="error" label="Late" />;
      }
      break;
    case 'unscheduled':
      backgroundStyle = 'removed';
      break;
  }
  return (
    <>
      {scheduleOpen && (
        <AttachWorkflowToEventDialog
          open={scheduleOpen}
          handleClose={handleClose('schedule')}
          userId={assignedWorkflowPhase.userId}
          description={assignedWorkflowPhase?.workflowPhase?.description}
          assignedWorkflowPhaseId={assignedWorkflowPhase.id}
        />
      )}
      {workflowOpen && (
        <WorkflowCompletionDialog
          open={workflowOpen}
          assignedPhaseId={assignedWorkflowPhase?.id}
          phase={assignedWorkflowPhase?.workflowPhase}
          responseId={assignedWorkflowPhase?.surveyResponseId}
          handleClose={handleClose('workflow')}
        />
      )}
      {learnOpen && (
        <LearnMoreDialog
          open={learnOpen}
          handleClose={handleClose('learn')}
          assignedWorkflowPhase={assignedWorkflowPhase}
        />
      )}

      <InfoCard
        variant="task"
        timeAsDue
        backgroundStyle={backgroundStyle}
        includeTime={false}
        startTime={assignedWorkflowPhase.dueDate}
        subject={assignedWorkflowPhase.workflowPhase?.title}
        primaryOption={primaryOption}
        secondaryOption={secondaryOption}
        statusChips={[chip]}
      >
        <>{description}</>
      </InfoCard>
    </>
  );
};

// Memoize to prevent re-renders when props haven't changed
const WorkflowCard = memo(WorkflowCardComponent);

export default WorkflowCard;
